import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './OverviewPage.css'

const API = process.env.REACT_APP_API_URL || ""

const VARIANTS = [
    {
        id: 'agri',
        label: 'AGRI',
        path: '/our-businesses/agri',
        accent: '#16a34a',
        accentRgb: '22,163,74',
        tag: 'Agriculture',
        tagline: 'Fly smarter. Spray better.',
        desc: 'Plan spray missions, avoid obstacles, monitor live and review every flight. Built for agricultural drone operators across India.',
        features: [
            'Auto waypoint generation',
            'Field boundary drawing',
            'Live spray telemetry',
            'DGCA-ready flight logs',
        ],
        hudLine: 'WAYPOINTS 1-28 · SPRAY: ACTIVE · FLOW: 1 L/MIN',
        mediaKey: 'agri_hero_bg',
        num: '01',
    },
    {
        id: 'survey',
        label: 'SURVEY',
        path: '/our-businesses/survey',
        accent: '#4a6fa5',
        accentRgb: '74,111,165',
        tag: 'Mapping & Survey',
        tagline: 'Map anything. Miss nothing.',
        desc: 'Auto-plan photogrammetry grid flights, manage GCPs on-site and export directly into your GIS pipeline with centimetre accuracy.',
        features: [
            'Photogrammetry grid planning',
            'GCP marker management',
            'Contour preview live',
            'KML / GeoJSON export',
        ],
        hudLine: 'GRID MISSION · OVERLAP: 90% · GSD: 2.1 CM/PX',
        mediaKey: 'survey_hero_bg',
        num: '02',
    },
    {
        id: 'camera',
        label: 'CAMERA',
        path: '/our-businesses/camera',
        accent: '#d97706',
        accentRgb: '217,119,6',
        tag: 'Aerial Photography',
        tagline: 'Every frame. Perfect.',
        desc: 'Plan cinematic orbit shots, control your camera live and keep every frame GPS-tagged. Built for aerial photographers and filmmakers.',
        features: [
            'Orbit & POI flight modes',
            'Live camera control',
            'Focus peaking overlay',
            'GPS-tagged shot library',
        ],
        hudLine: 'ORBIT MISSION · 4K · 60FPS · GIMBAL: -30°',
        mediaKey: 'camera_hero_bg',
        num: '03',
    },
]

const COMMON = [
    {
        icon: '◈',
        label: 'One Interface',
        desc: 'Same clean UI across all three modes. No learning curve when you switch missions.',
    },
    {
        icon: '◉',
        label: 'Offline Ready',
        desc: 'Plan and fly without internet. All mission data stored locally on your device.',
    },
    {
        icon: '◎',
        label: 'DGCA Compliant',
        desc: 'Pilot data, flight logs and mission records stored in DGCA-ready format automatically.',
    },
    {
        icon: '◍',
        label: 'Android Native',
        desc: 'Built for Android tablets and phones. Works with all major drone brands.',
    },
]

function VariantCard({ v, media, index }) {
    const ref = useRef(null)
    const [vis, setVis] = useState(false)

    useEffect(() => {
        const o = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setVis(true) },
            { threshold: 0.12 }
        )
        if (ref.current) o.observe(ref.current)
        return () => o.disconnect()
    }, [])

    return (
        <div
            ref={ref}
            className={`ov-card${vis ? ' ov-card-in' : ''}`}
            style={{
                '--card-accent': v.accent,
                '--card-rgb': v.accentRgb,
                transitionDelay: `${index * 0.1}s`,
            }}
        >
            <span className="ov-card-num ov-mono">{v.num}</span>

            {/* Image side */}
            <div className="ov-card-img-wrap">
                {media[v.mediaKey]
                    ? <img src={media[v.mediaKey]} alt={v.label} className="ov-card-img" />
                    : (
                        <div className="ov-card-img-empty ov-mono">
                            {v.mediaKey}
                        </div>
                    )
                }
                <div className="ov-card-img-fade" />
                <div className="ov-hud-strip ov-mono">{v.hudLine}</div>
                <div className="ov-card-badge ov-mono">{v.label}</div>
            </div>

            {/* Body side */}
            <div className="ov-card-body">
                <span className="ov-card-tag ov-mono">{v.tag}</span>
                <h3 className="ov-card-tagline">{v.tagline}</h3>
                <p className="ov-card-desc">{v.desc}</p>
                <ul className="ov-card-features">
                    {v.features.map(f => (
                        <li key={f} className="ov-card-feature ov-mono">
                            <span className="ov-feat-dot" />
                            {f}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}

export default function OverviewPage() {
    const [media, setMedia] = useState({})
    const [heroIn, setHeroIn] = useState(false)
    const commonRefs = useRef([])
    const [commonVis, setCommonVis] = useState([])

    useEffect(() => {

        document.title = "Drone Commander GCS Overview | Aviatricks"
        const metaDesc = document.querySelector('meta[name="description"]')
        if (metaDesc) {
            metaDesc.setAttribute("content", "Explore Drone Commander GCS: A single ground control app for agricultural spraying, land surveying, and aerial photography by Aviatricks.")
        }

        Promise.all([
            fetch(`${API}/api/media/agri`).then(r => r.json()),
            fetch(`${API}/api/media/survey`).then(r => r.json()),
            fetch(`${API}/api/media/camera`).then(r => r.json()),
        ]).then(([agri, survey, camera]) => {
            setMedia({ ...agri, ...survey, ...camera })
        }).catch(() => { })

        setTimeout(() => setHeroIn(true), 300)
    }, [])

    useEffect(() => {
        const obs = commonRefs.current.map((el, i) => {
            if (!el) return null
            const o = new IntersectionObserver(
                ([e]) => { if (e.isIntersecting) setCommonVis(v => [...new Set([...v, i])]) },
                { threshold: 0.15 }
            )
            o.observe(el)
            return o
        })
        return () => obs.forEach(o => o && o.disconnect())
    }, [])

    return (
        <div className="ov-wrap">

            {/* ── NAV ── */}
            <nav className="ov-nav">
                <div className="ov-nav-inner">
                    <div className="ov-nav-tabs ov-mono">
                        <Link to="/our-businesses/agri" className="ov-nav-tab">AGRI</Link>
                        <Link to="/our-businesses/survey" className="ov-nav-tab">SURVEY</Link>
                        <Link to="/our-businesses/camera" className="ov-nav-tab">CAMERA</Link>
                    </div>
                </div>
            </nav>

            {/* ── HERO ── */}
            <section className="ov-hero">
                <div className={`ov-hero-inner${heroIn ? ' ov-hero-in' : ''}`}>

                    <div className="ov-hero-eyebrow ov-mono">
                        <span className="ov-hero-pip" />
                        DRONE COMMANDER GCS
                    </div>

                    <h1 className="ov-hero-h1">
                        <span className="ov-hero-line ov-hl1">One app.</span>
                        <span className="ov-hero-line ov-hl2">Three missions.</span>
                    </h1>

                    <p className="ov-hero-p">
                        Drone Commander GCS is a single ground control app that handles agricultural spraying,
                        land surveying and aerial photography — all from the same clean interface.
                    </p>

                    <div className="ov-hero-badges">
                        {VARIANTS.map((v, i) => (
                            <div
                                key={v.id}
                                className="ov-hero-badge ov-mono"
                                style={{
                                    '--badge-color': v.accent,
                                    '--badge-rgb': v.accentRgb,
                                    animationDelay: `${0.7 + i * 0.12}s`,
                                }}
                            >
                                <span className="ov-hero-badge-dot" />
                                {v.label}
                            </div>
                        ))}
                    </div>

                </div>

                <div className="ov-ring ov-ring-1" />
                <div className="ov-ring ov-ring-2" />
                <div className="ov-ring ov-ring-3" />
            </section>

            {/* ── VARIANT CARDS ── */}
            <section className="ov-variants" id="variants">
                <div className="ov-variants-inner">
                    {VARIANTS.map((v, i) => (
                        <VariantCard key={v.id} v={v} media={media} index={i} />
                    ))}
                </div>
            </section>

            {/* ── COMMON FEATURES ── */}
            <section className="ov-common">
                <div className="ov-common-inner">
                    <div className="ov-common-head">
                        <span className="ov-common-eyebrow ov-mono">Across all variants</span>
                        <h2 className="ov-common-h2">Built once.<br />Works everywhere.</h2>
                    </div>
                    <div className="ov-common-grid">
                        {COMMON.map((c, i) => (
                            <div
                                key={c.label}
                                ref={el => commonRefs.current[i] = el}
                                className={`ov-common-card${commonVis.includes(i) ? ' ov-common-in' : ''}`}
                                style={{ transitionDelay: `${i * 0.1}s` }}
                            >
                                <span className="ov-common-icon">{c.icon}</span>
                                <h3 className="ov-common-title">{c.label}</h3>
                                <p className="ov-common-desc">{c.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="ov-cta">
                <div className="ov-cta-inner">
                    <span className="ov-cta-label ov-mono">Drone Commander GCS</span>
                    <h2 className="ov-cta-h2">One app for<br />every mission.</h2>
                    <p className="ov-cta-p">
                        Get Drone Commander GCS for your drone operation.
                        Available for all mission types.
                    </p>
                    <div className="ov-cta-btns">
                        <Link to="/contact/support" className="ov-cta-btn-primary">
                            Request a Demo
                        </Link>
                        <Link to="/our-businesses/agri" className="ov-cta-btn-ghost">
                            Start with Agri →
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    )
}